import {
  requestDecryptionKeyForTransaction,
  pollForDecryptionKey,
  extractDecryptionKeyFromResponse,
  isLikelyEciesCiphertext,
  checkExistingKeyRequest,
  fetchActiveMasterPubkey
} from './keyService';
import { decryptIbeKeyFromKvEntry } from './kvDecryptionService';
import { getPrivateKeyFromSession } from './cryptoKeyService';

/**
 * Request (or reuse) a PEP decryption-key kv entry for an IBE identity, unlock it
 * with the auditor's session private key, and return the IBE sk.
 *
 * Identity is typically `address.toLowerCase() + chainPk` (`*RandomnessId` on the
 * grant). Callers should cache by identity so bulk decrypt does not re-request.
 *
 * @param {Object} params
 * @param {string} params.pepIdentity - Identity sent to PEP / used in kv paths
 * @param {string} params.auditorIdentity - Auditor UID / grant id
 * @param {string} params.secpPublicKey - Auditor SECP pubkey
 * @param {Object} [params.lookupHints] - Optional kv hints (tx hash, request id, extra auditor ids)
 * @param {AbortSignal} [params.abortSignal]
 * @param {(phase: 'polling'|'unlocking') => void} [params.onPhase]
 * @returns {Promise<string>} Unlocked IBE private key for this identity
 */
export const obtainUnlockedIbeKeyForIdentity = async ({
  pepIdentity,
  auditorIdentity,
  secpPublicKey,
  lookupHints = {},
  abortSignal = null,
  onPhase = null
} = {}) => {
  if (!pepIdentity) {
    throw new Error('PEP identity is required');
  }
  if (!auditorIdentity) {
    throw new Error('Auditor identity is required');
  }
  if (!secpPublicKey) {
    throw new Error('Auditor public key is required');
  }

  const report = (phase) => {
    try {
      onPhase?.(phase);
    } catch {
      // ignore UI callback errors
    }
  };

  report('polling');
  const activeMasterPubkey =
    lookupHints.requiredMasterPubkey || (await fetchActiveMasterPubkey());

  const kvLookupHints = {
    ...lookupHints,
    requiredMasterPubkey: activeMasterPubkey,
    forceNewRequest: undefined
  };

  const pollAbort = new AbortController();
  const onOuterAbort = () => pollAbort.abort();
  if (abortSignal) {
    if (abortSignal.aborted) {
      throw new Error('Polling aborted');
    }
    abortSignal.addEventListener('abort', onOuterAbort, { once: true });
  }

  let pollPromise = pollForDecryptionKey(auditorIdentity, pepIdentity, {
    interval: 4000,
    timeout: 300000,
    fast: true,
    ...kvLookupHints,
    abortSignal: pollAbort.signal
  }).catch((pollError) => {
    if (pollError?.message === 'Polling aborted') return null;
    throw pollError;
  });

  let result;
  try {
    result = await requestDecryptionKeyForTransaction(
      pepIdentity,
      secpPublicKey,
      auditorIdentity,
      kvLookupHints
    );
  } catch (requestError) {
    pollAbort.abort();
    throw requestError;
  }

  const pollHints = {
    ...kvLookupHints,
    requiredMasterPubkey: result.masterPubkey || activeMasterPubkey,
    minSucceededAt: result.minSucceededAt || null
  };

  const unlockKvPayload = async (kvPayload) => {
    report('unlocking');
    const decryptionKey = extractDecryptionKeyFromResponse(kvPayload.value, {
      fullResult: kvPayload
    });
    if (decryptionKey && !isLikelyEciesCiphertext(decryptionKey)) {
      return decryptionKey;
    }
    const sessionKey = getPrivateKeyFromSession();
    if (!sessionKey) {
      throw new Error('Session private key not found. Please log out and log in again.');
    }
    return decryptIbeKeyFromKvEntry(kvPayload, sessionKey, decryptionKey);
  };

  try {
    if (result.exists) {
      pollAbort.abort();
      return await unlockKvPayload(result.data);
    }

    if (result.data?.tx_hash) {
      const withTx = await checkExistingKeyRequest(auditorIdentity, pepIdentity, {
        ...pollHints,
        txHash: result.data.tx_hash,
        fast: true
      });
      if (withTx?.value) {
        pollAbort.abort();
        return await unlockKvPayload(withTx);
      }
    }

    let keyData = null;
    if (!pollPromise || pollHints.minSucceededAt) {
      if (pollPromise) pollAbort.abort();
      const freshAbort = new AbortController();
      if (abortSignal) {
        abortSignal.addEventListener('abort', () => freshAbort.abort(), { once: true });
      }
      keyData = await pollForDecryptionKey(auditorIdentity, pepIdentity, {
        interval: 4000,
        timeout: 300000,
        fast: true,
        ...pollHints,
        abortSignal: freshAbort.signal
      });
    } else {
      keyData = await pollPromise;
    }
    if (!keyData) {
      throw new Error('Polling aborted before key was found');
    }
    return await unlockKvPayload(keyData);
  } finally {
    if (abortSignal) {
      abortSignal.removeEventListener('abort', onOuterAbort);
    }
  }
};
